import { Stack, Title, Text, Group, Button, Paper, SegmentedControl, ThemeIcon, Switch, Chip } from '@mantine/core';
import { IconChefHat, IconArrowRight, IconArrowLeft } from '@tabler/icons-react';

type CookingPreferencesStepProps = {
  value: {
    cookingSkill: 'beginner' | 'intermediate' | 'advanced';
    includeLeftovers: boolean;
    preferredCookingMethods?: string[];
  };
  onChange: (value: {
    cookingSkill: 'beginner' | 'intermediate' | 'advanced';
    includeLeftovers: boolean;
    preferredCookingMethods?: string[];
  }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isCompleting?: boolean;
};

export const CookingPreferencesStep = ({ value, onChange, onNext, onBack, isLastStep }: CookingPreferencesStepProps) => {
  const preferredCookingMethods = value.preferredCookingMethods || [];

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconChefHat size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Cooking preferences
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            Tell us about your cooking style and meal planning preferences
          </Text>
        </Stack>
      </Stack>

      {/* Cooking Preferences */}
      <Paper 
        radius="lg" 
        p={{ base: 'md', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="xl">
          {/* Cooking Skill */}
          <Stack gap="md">
            <Text fw={600} size="lg">
              What's your cooking skill level?
            </Text>
            <Text size="sm" c="dimmed">We'll match recipe complexity to your comfort level</Text>
            
            {/* Mobile: Vertical */}
            <SegmentedControl
              value={value.cookingSkill}
              onChange={(val) => onChange({ ...value, cookingSkill: val as 'beginner' | 'intermediate' | 'advanced' })}
              data={[
                { 
                  label: 'Beginner', 
                  value: 'beginner',
                },
                { 
                  label: 'Intermediate', 
                  value: 'intermediate',
                },
                { 
                  label: 'Advanced', 
                  value: 'advanced',
                },
              ]}
              orientation="vertical"
              size="md"
              color="orange"
              fullWidth
              hiddenFrom="sm"
              styles={{
                control: {
                  padding: '8px 12px'
                }
              }}
            />
            
            {/* Desktop: Horizontal */}
            <SegmentedControl
              value={value.cookingSkill}
              onChange={(val) => onChange({ ...value, cookingSkill: val as 'beginner' | 'intermediate' | 'advanced' })}
              data={[
                { 
                  label: 'Beginner', 
                  value: 'beginner',
                },
                { 
                  label: 'Intermediate', 
                  value: 'intermediate',
                },
                { 
                  label: 'Advanced', 
                  value: 'advanced',
                },
              ]}
              orientation="horizontal"
              size="md"
              color="orange"
              visibleFrom="sm"
              styles={{
                control: {
                  padding: '8px 12px'
                }
              }}
            />
          </Stack>

          {/* Leftovers Preference */}
          <Stack gap="md">
            <Text fw={600} size="lg">
              Leftovers preference
            </Text>
            <Text size="sm" c="dimmed">Should we plan larger portions to provide leftovers?</Text>

            <Switch
              checked={value.includeLeftovers}
              onChange={(event) => onChange({ ...value, includeLeftovers: event.currentTarget.checked })}
              label="Include leftovers"
              size="md"
              color="orange"
              styles={{
                root: {
                  alignSelf: 'flex-start'
                }
              }}
            />
          </Stack>

          {/* Preferred Cooking Methods */}
          <Stack gap="md">
            <Text fw={600} size="lg">
              Cooking style preferences
            </Text>
            <Text size="sm" c="dimmed">How do you prefer to cook? (Select all that apply)</Text>

            <Chip.Group multiple value={preferredCookingMethods} onChange={(values) => onChange({ ...value, preferredCookingMethods: values })}>
              <Group gap="xs" className="chip-cloud">
                <Chip value="one_pot" color="orange" variant="filled">One-pot / Sheet pan meals</Chip>
                <Chip value="set_forget" color="orange" variant="filled">Set-and-forget (slow cooker)</Chip>
                <Chip value="stovetop" color="orange" variant="filled">Stovetop cooking</Chip>
                <Chip value="grilling" color="orange" variant="filled">Grilling / BBQ</Chip>
                <Chip value="baking" color="orange" variant="filled">Baking / Roasting</Chip>
                <Chip value="no_cook" color="orange" variant="filled">No-cook / Minimal prep</Chip>
              </Group>
            </Chip.Group>
          </Stack>
        </Stack>
      </Paper>

      {/* Summary */}
      <Paper
        p="lg"
        radius="lg"
        bg="gray.0"
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          minHeight: 80,
          width: '100%'
        }}
      >
        <Group gap="md" align="center" style={{ height: '100%' }}>
          <ThemeIcon
            size={40}
            radius="md"
            color="green"
            variant="light"
          >
            <IconChefHat size={20} />
          </ThemeIcon>

          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} c="green.7" style={{ wordBreak: 'break-word' }}>
              Cooking preferences saved!
            </Text>
            <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
              We'll suggest {value.cookingSkill} recipes
              {value.includeLeftovers ? ' and include intentional leftovers' : ''}
              {preferredCookingMethods.length > 0 ? ` with your preferred cooking styles` : ''}
            </Text>
          </Stack>
        </Group>
      </Paper>

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button 
          variant="subtle" 
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          color="orange"
        >
          Back
        </Button>
        
        <Button 
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={onNext}
          color="orange"
          variant="filled"
        >
          {isLastStep ? 'Finish' : 'Continue'}
        </Button>
      </Group>
    </Stack>
  );
};
